import classNames from 'classnames'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { CalendarDragProvider } from '../dnd'
import {
  buildCalendarBars,
  buildChipsByDay,
  buildContentSpans,
  buildDayChipOrder,
  buildDayRows,
  computeDayBudget,
  computeLaneCap,
  computeWeekRowBlockSize,
  dayRowBlockSize,
  fillWeekRowBlockSize,
  placeBarsInWeek,
  placeSpansInWeek,
  runStartRows,
  type DayBudget,
  type DayChipOrder,
  type PlacedBar,
  type PlacedSpan,
} from '../laneLayout'
import { buildMonthGrid, DAYS_IN_WEEK } from '../monthGrid'
import { buildQuickfindMatcher } from '../quickfind'
import { indexItemsById, resolveItemColor } from '../resolveItemColor'
import { cardBlockSize, CalendarSizes } from '../sizes'
import { type CalendarItem } from '../types'
import { DayCell } from './DayCell'
import * as styles from './styles'
import { type MonthCalendarGridProps } from './types'
import { useDayFocus } from './useDayFocus'

export { selectors as monthCalendarSelectors } from './styles'

const EMPTY_CHIPS: readonly CalendarItem[] = []
const EMPTY_ORDER: DayChipOrder = { chips: EMPTY_CHIPS, runStartIds: new Set() }
const EMPTY_ROWS: readonly number[] = []

/** Everything one day cell needs that the week row worked out for it. */
type DayLayout = {
  bars: PlacedBar[]
  budget: DayBudget
  hiddenItems: CalendarItem[]
  reservedRows: number[]
  spans: PlacedSpan[]
}

/**
 * Whether a day draws a piece of the item: a bar or a span that starts on it.
 * Only those days mark the hovered item, so only they are handed its id.
 */
const holdsPieceOf = (
  layout: DayLayout | undefined,
  itemId: string | undefined
): boolean =>
  itemId !== undefined &&
  layout !== undefined &&
  (layout.bars.some((bar) => bar.item.id === itemId) ||
    layout.spans.some((span) => span.item.id === itemId))

/**
 * How many placeholder chips each day of a first paint carries, counted from
 * the first day of the grid. Nine numbers over a seven-day week means the
 * pattern never settles into columns, and a fixed list means a month never
 * shimmers two different ways.
 */
const SKELETON_CHIPS = [1, 0, 2, 1, 1, 0, 2, 1, 0]

const buildFormatters = (locale: IETFLanguageTag | undefined) => ({
  date: new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
    year: 'numeric',
  }),
  dayNumber: new Intl.DateTimeFormat(locale, { day: 'numeric' }),
  shortDate: new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
  }),
  timeZone: new Intl.DateTimeFormat(locale, { timeZoneName: 'short' }),
  weekday: new Intl.DateTimeFormat(locale, { weekday: 'short' }),
})

/**
 * The month grid. It draws what it is given and reports what a person did with
 * it: no fetching, no routing, no workspace context. That is what lets the same
 * component serve the main calendar today and a plan or block tab later.
 */
const MonthCalendarGridContent: React.FC<MonthCalendarGridProps> = ({
  barKinds,
  chipKinds,
  className,
  dayTooltip,
  display,
  items,
  loading = 'idle',
  locale,
  menu,
  month,
  onDayOpen,
  onEscape,
  onItemClick,
  onItemDrop,
  onItemHover,
  onOverflowClick,
  openItemId,
  referenceItems,
  renderChannelIcon,
  renderDayActions,
  renderStatusIcon,
  resolveChosenColor,
  resolveDisplay,
  spanKinds,
  today,
  weekStartsOn,
}) => {
  const formatters = useMemo(() => buildFormatters(locale), [locale])
  const density = display?.density
  const sortBy = display?.sortBy ?? 'none'
  const shouldShowMomentBadge = display?.shouldShowMomentBadge
  const isScrolling = display?.weeksMode === 'scroll'
  const shouldShowLabelColor = display?.shouldShowLabelColor
  const itemBlockSize =
    density === 'card'
      ? cardBlockSize({ shouldShowMomentBadge })
      : CalendarSizes.chipBlockSize

  const weeks = useMemo(
    () => buildMonthGrid({ month, today: today ?? new Date(), weekStartsOn }),
    [month, today, weekStartsOn]
  )

  // How tall the grid is, reported by the browser. It is `undefined` until the
  // first report, and wherever nothing reports at all, which is what the
  // standard row height answers for.
  //
  // Only a month of fixed rows shares that height out between its weeks. Its
  // rows can never come to more than the height they were shared out of, so
  // what it measures is never a result of what it drew.
  //
  // A scrolling week grows past whatever floor it is given. A floor taken from
  // the measurement would therefore raise the height it was measured from,
  // round after round, wherever the page leaves the grid to size itself around
  // its weeks. A scrolling month fills the room it has in CSS instead: see
  // `flexGrow` on a scrolling week in `styles.ts`.
  const [gridBlockSize, setGridBlockSize] = useState<number>()
  const rowBlockSize =
    display?.weekRowBlockSize ??
    (gridBlockSize === undefined || isScrolling
      ? CalendarSizes.weekRowBlockSize
      : fillWeekRowBlockSize({
          availableBlockSize: gridBlockSize - CalendarSizes.weekdayRowBlockSize,
          weekCount: weeks.length,
        }))

  const bars = useMemo(
    () => buildCalendarBars(items, { kinds: barKinds }),
    [barKinds, items]
  )

  const spans = useMemo(
    () =>
      buildContentSpans(items, {
        kinds: spanKinds,
        shouldShowInMarketBars: display?.shouldShowInMarketBars,
      }),
    [display?.shouldShowInMarketBars, items, spanKinds]
  )

  // A content item drawn across its in-market days has already said where it
  // is. Its chip on the launch day would be the same item a second time.
  const idsDrawnAsSpans = useMemo(
    () => new Set(spans.map((span) => span.item.id)),
    [spans]
  )

  const chipsByDay = useMemo(
    () => buildChipsByDay(items, { idsDrawnAsSpans, kinds: chipKinds }),
    [chipKinds, idsDrawnAsSpans, items]
  )

  // Reference items first, so anything actually drawn wins its own id.
  const itemsById = useMemo(
    () => indexItemsById([...(referenceItems ?? []), ...items]),
    [items, referenceItems]
  )
  const colorFor = useCallback(
    (item: CalendarItem) =>
      resolveItemColor(item, itemsById, resolveChosenColor),
    [itemsById, resolveChosenColor]
  )
  const parentOf = useCallback(
    (item: CalendarItem) =>
      item.parentId === undefined ? undefined : itemsById.get(item.parentId),
    [itemsById]
  )

  const chipOrderByDay = useMemo(() => {
    const orders = new Map<string, DayChipOrder>()
    chipsByDay.forEach((chips, dayKey) =>
      orders.set(dayKey, buildDayChipOrder(chips, sortBy, { parentOf }))
    )
    return orders
  }, [chipsByDay, parentOf, sortBy])

  /**
   * One pass over the month that hands every day its own share of the week
   * row's height. A day cannot budget alone: the lanes it gives up belong to
   * the whole row, and a span is only drawn when every day it crosses can hold
   * its row, so the whole week is worked out at once.
   */
  const { dayLayouts, laneCounts, weekBlockSizes } = useMemo(() => {
    const dayLayouts = new Map<string, DayLayout>()
    const laneCounts = new Map<string, number>()
    const weekBlockSizes = new Map<string, number>()

    const rowHeight = dayRowBlockSize({ density, shouldShowMomentBadge })
    const laneCap = isScrolling
      ? undefined
      : computeLaneCap({ rowBlockSize, rowHeight })

    weeks.forEach((week) => {
      const lanes = placeBarsInWeek(bars, week, { laneCap })
      const placedSpans = placeSpansInWeek(spans, week)
      const weekBlockSize = isScrolling
        ? computeWeekRowBlockSize({
            laneCount: lanes.laneCount,
            rowBlockSize,
            rowHeight,
          })
        : rowBlockSize

      laneCounts.set(week.key, lanes.laneCount)
      weekBlockSizes.set(week.key, weekBlockSize)

      const dayKeys = week.days.map((day) => day.key)
      const hiddenByDay = new Map<string, CalendarItem[]>(
        dayKeys.map((key) => [key, []])
      )
      const coveredDays = (run: { span: number; startColumn: number }) =>
        dayKeys.slice(run.startColumn - 1, run.startColumn - 1 + run.span)

      // A moment whose lane the row had no room for is still part of every day
      // it covers, so each of them counts it behind "+N more".
      lanes.hiddenBars.forEach((bar) =>
        coveredDays(bar).forEach((key) => hiddenByDay.get(key)?.push(bar.item))
      )

      const budgetFor = (dayKey: string, reservedRows: readonly number[]) => {
        const order = chipOrderByDay.get(dayKey) ?? EMPTY_ORDER
        const rows = buildDayRows(reservedRows, order.chips)
        const runStarts = runStartRows(rows, {
          reservedRows,
          runStartIds: order.runStartIds,
        })

        return isScrolling
          ? { visibleCount: rows.length }
          : computeDayBudget({
              // What the day cannot draw at all still owes it a "+N more" row.
              hasHiddenItems: (hiddenByDay.get(dayKey)?.length ?? 0) > 0,
              laneCount: lanes.laneCount,
              rowBlockSize: weekBlockSize,
              rowHeights: rows.map((row, index) =>
                row === null
                  ? rowHeight
                  : rowHeight +
                    (runStarts.has(index) ? CalendarSizes.runGap : 0)
              ),
            })
      }

      const rowsHeldBy = (spans: readonly PlacedSpan[]) => {
        const held = new Map<string, number[]>()
        spans.forEach((span) =>
          coveredDays(span).forEach((key) => {
            const rows = held.get(key)
            if (rows) rows.push(span.row)
            else held.set(key, [span.row])
          })
        )
        return held
      }

      // A span is one straight line across its days, so it is drawn only where
      // every day it crosses can hold its row — measured against the very
      // column the day will be drawn with.
      //
      // Holding a span back changes two things about that column: the row it
      // gave up comes back, and the day now owes a "+N more" row for what it
      // cannot draw. Either can decide another span, so the pass runs again
      // until it holds nothing new back. It only ever holds back more, so it
      // always settles.
      let drawnSpans: readonly PlacedSpan[] = placedSpans.spans

      for (;;) {
        const heldRows = rowsHeldBy(drawnSpans)
        const kept = drawnSpans.filter((span) =>
          coveredDays(span).every(
            (key) =>
              budgetFor(key, heldRows.get(key) ?? []).visibleCount > span.row
          )
        )
        if (kept.length === drawnSpans.length) break

        const keptKeys = new Set(kept.map((span) => span.key))
        drawnSpans
          .filter((span) => !keptKeys.has(span.key))
          .forEach((span) =>
            coveredDays(span).forEach((key) =>
              hiddenByDay.get(key)?.push(span.item)
            )
          )
        drawnSpans = kept
      }

      const reservedRowsByDay = rowsHeldBy(drawnSpans)

      week.days.forEach((day, dayIndex) => {
        // Only the spans a person can see hold a row open. A row kept for a
        // span nobody is shown would be a blank line in the day.
        const reservedRows = reservedRowsByDay.get(day.key) ?? []

        dayLayouts.set(day.key, {
          bars: lanes.bars.filter((bar) => bar.startColumn === dayIndex + 1),
          budget: budgetFor(day.key, reservedRows),
          hiddenItems: hiddenByDay.get(day.key) ?? [],
          reservedRows,
          spans: drawnSpans.filter((span) => span.startColumn === dayIndex + 1),
        })
      })
    })

    return { dayLayouts, laneCounts, weekBlockSizes }
  }, [
    bars,
    chipOrderByDay,
    density,
    isScrolling,
    rowBlockSize,
    shouldShowMomentBadge,
    spans,
    weeks,
  ])

  // The panel builds the same range from its own formatters, but grid-core sits
  // below the panel in the stack and cannot reach them.
  const formatDateRange = useCallback(
    (start: Date, end: Date) =>
      start.getTime() === end.getTime()
        ? formatters.shortDate.format(start)
        : `${formatters.shortDate.format(start)} – ${formatters.shortDate.format(end)}`,
    [formatters]
  )

  // The zone a time is read in. The month payload carries none of its own, so
  // it is the zone the person is sitting in — and it is worked out per item,
  // because the same place is on daylight time for half the year.
  const formatTimeZone = useCallback(
    (instant: string) =>
      formatters.timeZone
        .formatToParts(new Date(instant))
        .find((part) => part.type === 'timeZoneName')?.value,
    [formatters]
  )

  const quickfindMatcher = useMemo(
    () => buildQuickfindMatcher(display?.quickfind),
    [display?.quickfind]
  )
  const isDimmed = useCallback(
    (item: CalendarItem) =>
      quickfindMatcher !== null && !quickfindMatcher(item),
    [quickfindMatcher]
  )

  // Which day the pointer is on, read from where the pointer is rather than
  // from what it is over. A bar is drawn inside the cell of the day it starts
  // on and reaches over the days after it, so the element under the pointer
  // belongs to another day as often as not, and the browser's own `:hover`
  // leaves and re-enters a cell as the pointer crosses the bars in it.
  const [hoveredKey, setHoveredKey] = useState<string>()
  // A moment or an in-market window is drawn once per week row it reaches, so
  // the pointer resting on any one piece has to mark the rest. The id lives
  // over the whole grid and only changes classes: the layout is untouched, so
  // nothing is measured or placed again on hover.
  const [hoveredItemId, setHoveredItemId] = useState<string>()

  // An item that leaves the month while the pointer is on it never says it
  // was left: the element that would have answered is gone. Without this, its
  // id would still be the hovered one when the same item came back — filtered
  // in again, or refetched — and it would be drawn highlighted with the
  // pointer somewhere else entirely.
  useEffect(() => setHoveredItemId(undefined), [items])

  const hoverItem = useCallback(
    (itemId: string | undefined) => {
      setHoveredItemId(itemId)
      onItemHover?.(itemId)
    },
    [onItemHover]
  )

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    const week = (event.target as Element).closest(`.${styles.selectors.week}`)
    const day = Array.from(week?.children ?? []).find((cell) => {
      const { left, right } = cell.getBoundingClientRect()
      return event.clientX >= left && event.clientX < right
    })
    setHoveredKey(day?.getAttribute('data-date') ?? undefined)
  }, [])

  const { focusedKey, handleKeyDown, rootRef } = useDayFocus({
    onDayOpen,
    onEscape,
    weeks,
  })

  // The one place the grid reads the DOM. What it measures is an argument to
  // the layout, never something the layout goes looking for, so the budget
  // stays a pure function of the numbers it is handed. It reads before the
  // browser paints, so a fixed month never shows a frame of standard rows and
  // then reflows. Whole pixels only: a sub-pixel resize would redraw every
  // cell of the month for a difference nobody can see.
  useLayoutEffect(() => {
    const root = rootRef.current
    if (root === null) return

    // A height of nothing is not a measurement — the grid is hidden, or it has
    // no layout yet — and a month drawn for it would be a month nobody asked
    // for, so the rows keep the height they already have.
    const measure = (blockSize: number) => {
      if (blockSize > 0) setGridBlockSize(Math.floor(blockSize))
    }

    measure(root.clientHeight)
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(([entry]) =>
      measure(entry.contentRect.height)
    )
    observer.observe(root)
    return () => observer.disconnect()
  }, [rootRef])

  return (
    <div
      ref={rootRef}
      aria-busy={loading !== 'idle'}
      aria-label="Month calendar"
      className={classNames(
        styles.selectors.root,
        display?.weeksMode === 'scroll' && styles.stateVariables.scrollWeeks,
        className
      )}
      css={styles.root}
      role="grid"
      // Focusable for the grid role's sake, but out of the tab order: the
      // day cells carry the roving tab stop.
      tabIndex={-1}
      style={
        {
          [styles.cssVariables.cardBlockSize]: `${cardBlockSize({
            shouldShowMomentBadge,
          })}px`,
          [styles.cssVariables.itemBlockSize]: `${itemBlockSize}px`,
          [styles.cssVariables.weekRowBlockSize]: `${rowBlockSize}px`,
        } as CSSProperties
      }
      onMouseLeave={() => {
        setHoveredKey(undefined)
        // The pointer has left the grid, so it is on nothing the grid draws.
        setHoveredItemId(undefined)
      }}
      onMouseMove={handleMouseMove}
    >
      {loading === 'refetching' && (
        <div
          aria-label="Refreshing the month"
          className={styles.selectors.progress}
          role="progressbar"
        />
      )}

      <div className={styles.selectors.weekdays} role="row">
        {weeks[0].days.map((day) => (
          <div
            key={day.key}
            className={styles.selectors.weekday}
            role="columnheader"
          >
            {formatters.weekday.format(day.date)}
          </div>
        ))}
      </div>

      {weeks.map((week, weekIndex) => (
        <div
          key={week.key}
          className={styles.selectors.week}
          role="row"
          style={
            {
              [styles.cssVariables.laneCount]: laneCounts.get(week.key) ?? 0,
              [styles.cssVariables.weekRowBlockSize]: `${
                weekBlockSizes.get(week.key) ?? rowBlockSize
              }px`,
            } as CSSProperties
          }
        >
          {week.days.map((day, dayIndex) => {
            const layout = dayLayouts.get(day.key)
            const order = chipOrderByDay.get(day.key) ?? EMPTY_ORDER

            return (
              <DayCell
                key={day.key}
                bars={layout?.bars ?? []}
                budget={layout?.budget ?? { visibleCount: 0 }}
                chips={order.chips}
                colorFor={colorFor}
                dateLabel={formatters.date.format(day.date)}
                day={day}
                dayNumber={formatters.dayNumber.format(day.date)}
                dayTooltip={dayTooltip}
                density={density}
                formatDateRange={formatDateRange}
                formatTimeZone={formatTimeZone}
                hiddenItems={layout?.hiddenItems ?? EMPTY_CHIPS}
                hoveredItemId={
                  holdsPieceOf(layout, hoveredItemId)
                    ? hoveredItemId
                    : undefined
                }
                isDimmed={isDimmed}
                openItemId={openItemId}
                isFocused={day.key === focusedKey}
                isHovered={day.key === hoveredKey}
                mark={sortBy === 'account' ? 'account' : 'channel'}
                menu={menu}
                namePriority={display?.namePriority}
                parentOf={parentOf}
                renderChannelIcon={renderChannelIcon}
                renderDayActions={renderDayActions}
                renderStatusIcon={renderStatusIcon}
                reservedRows={layout?.reservedRows ?? EMPTY_ROWS}
                resolveDisplay={resolveDisplay}
                runStartIds={order.runStartIds}
                skeletonChips={
                  loading === 'initial'
                    ? SKELETON_CHIPS[
                        (weekIndex * DAYS_IN_WEEK + dayIndex) %
                          SKELETON_CHIPS.length
                      ]
                    : 0
                }
                shouldShowContentName={display?.shouldShowContentName}
                shouldShowLabelColor={shouldShowLabelColor}
                shouldShowMomentBadge={shouldShowMomentBadge}
                shouldShowTime={display?.shouldShowTime}
                spans={layout?.spans ?? []}
                onDayOpen={onDayOpen}
                onItemClick={onItemClick}
                onItemDrop={onItemDrop}
                onItemHover={hoverItem}
                onKeyDown={handleKeyDown}
                onOverflowClick={onOverflowClick}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}

/**
 * The drag state has to sit above every cell and chip, so the grid itself is
 * where it is provided. It costs nothing when nothing can be dragged.
 */
export const MonthCalendarGrid: React.FC<MonthCalendarGridProps> = (props) => (
  <CalendarDragProvider>
    <MonthCalendarGridContent {...props} />
  </CalendarDragProvider>
)
MonthCalendarGrid.displayName = 'Calendar: MonthCalendarGrid'
